import { useMemo } from 'react';
import { TimelineItem, ItemFilters, ActiveFunctions } from '@/types/item';
import { useItemCategories } from '@/hooks/useItemCategories';
import { useItemsCategoryPermissions } from '@/hooks/useItemsPermissions';
import { filterItemsByPermissionsAndFilters } from '@/lib/itemsFilter';
import { getTimelineRange, paginateTimeline, TimelinePage } from '@/utils/timelinePagination';
import { groupAndSortItemsByCategory } from '@/utils/itemsCategoryGrouping';
import { useItemsMilestones } from '@/hooks/milestones/useItemMilestones';
import { useMilestoneTypes } from '@/hooks/milestones/useMilestoneTypes';
import { useItemsDependsOn, useMilestonesDependsOn, type DependsOnRef } from '@/hooks/useDependsOn';
// ✅ RETIRED 2026-05-01 (PR-F3): globalFeatureFlags import removed (ENABLE_TIMELINE_REPORT_ASSESSMENT hardcoded true)
import { 
  calculateProjectHealth, 
  calculateCategoryPerformance,
  generateAssessmentIssues,
  type ProjectHealthAssessment,
  type CategoryPerformance,
  type AssessmentIssue,
  type ItemMilestone
} from '@/utils/timelineAssessment';

export interface TimelineReportData {
  filteredItems: TimelineItem[];
  pages: TimelinePage[];
  timelineRange: {
    startDate: string;
    endDate: string;
    totalDays: number;
  };
  statistics: {
    totalItems: number;
    totalDuration: number;
    averageDuration: number;
    projectCount?: number;
    categoryBreakdown: Array<{
      category: string;
      count: number;
      totalDuration: number;
      color: string;
    }>;
    projectBreakdown?: Array<{
      projectId: string;
      projectName: string;
      count: number;
      totalDuration: number;
    }>;
  };
  projectGroups?: Array<{
    projectId: string;
    projectName: string;
    items: TimelineItem[];
  }>;
  milestones?: any[];
  milestonesByItem?: Record<string, any[]>;
  milestoneTypes?: Array<{
    name?: string;
    display_name?: string;
    color?: string;
    description?: string | null;
    is_active?: boolean;
    sort_order?: number;
    created_at?: string;
    updated_at?: string;
  }>;
  assessment?: {
    projectHealth: ProjectHealthAssessment;
    categoryPerformance: CategoryPerformance[];
    issues: AssessmentIssue[];
  };
  itemDeps?: Map<string, DependsOnRef[]>;
  msDeps?: Map<string, DependsOnRef[]>;
}

interface UseTimelineReportDataOptions {
  items: TimelineItem[];
  filters?: ItemFilters;
  activeFunctions?: ActiveFunctions;
  selectedProject?: string;
  pageFormat?: 'portrait' | 'landscape';
  sortOrder?: 'current' | 'category';
  customDateRange?: { startDate: string; endDate: string };
  stretchToFullWidth?: boolean;
  showCategories?: boolean;
}

export const useTimelineReportData = ({
  items,
  filters = {
    search: '',
    activeGates: { g1: true, g2: true, g3: true, g4: true, g5: true }
  },
  activeFunctions = { shop: true, specs: true, po: true, contract: false },
  selectedProject,
  pageFormat = 'landscape',
  sortOrder = 'category',
  customDateRange,
  stretchToFullWidth = false,
  showCategories = true
}: UseTimelineReportDataOptions): TimelineReportData => {
  const { data: categoriesData = [] } = useItemCategories();
  const { canViewCategory } = useItemsCategoryPermissions();
  
  // ✅ HARDCODED (2025-11-09): Milestones always enabled
  const milestoneFeatureEnabled = true;
  const itemIds = useMemo(() => items.map(item => item.id), [items]);
  const { data: milestones = [] } = useItemsMilestones(
    milestoneFeatureEnabled ? itemIds : [],
    { enabled: milestoneFeatureEnabled && itemIds.length > 0 }
  );
  
  // Fetch milestone types for colors
  const { data: milestoneTypesData = [] } = useMilestoneTypes(
    { enabled: milestoneFeatureEnabled }
  );

  // depends_on (additive, for PDF export connector overlay)
  const { data: itemDependsRaw } = useItemsDependsOn(itemIds);
  const { data: msDependsRaw } = useMilestonesDependsOn(itemIds);

  const itemDeps = useMemo(() => {
    const m = new Map<string, DependsOnRef[]>();
    if (itemDependsRaw) {
      itemDependsRaw.forEach((deps, id) => {
        if (Array.isArray(deps) && deps.length) m.set(id, deps);
      });
    }
    return m;
  }, [itemDependsRaw]);

  const msDeps = useMemo(() => {
    const m = new Map<string, DependsOnRef[]>();
    if (msDependsRaw) {
      msDependsRaw.forEach((deps, id) => {
        if (Array.isArray(deps) && deps.length) m.set(id, deps);
      });
    }
    return m;
  }, [msDependsRaw]);

  if (import.meta.env.DEV && (itemDeps.size || msDeps.size)) {
    console.log('[export-deps]', itemDeps.size, msDeps.size);
  }

  const reportData = useMemo(() => {
    const isMultiProject = !selectedProject;
    
    // Filter items based on permissions and filters
    const rawFilteredItems = filterItemsByPermissionsAndFilters(items, {
      filters,
      activeFunctions,
      selectedProject: isMultiProject ? undefined : selectedProject,
      canViewCategory,
      enableDebugLog: false,
      logPrefix: 'TimelineReport'
    });

    // Apply sorting based on sortOrder setting
    // 'current': Preserve incoming order (matches on-screen)
    // 'category': Group by category and sort (default)
    const sortedItems = sortOrder === 'current'
      ? rawFilteredItems
      : groupAndSortItemsByCategory(rawFilteredItems, categoriesData);

    // Lọc item theo loại mốc (mirror tab Plan): rỗng → giữ nguyên; có chọn → chỉ item có ≥1 milestone thuộc type đó
    const activeMsTypes = filters?.activeMilestoneTypes;
    const filteredItems = (!activeMsTypes || activeMsTypes.length === 0)
      ? sortedItems
      : sortedItems.filter(item => {
          const ms = milestones.filter((m: any) => m.item_id === item.id);
          return ms.some((m: any) => activeMsTypes.includes(m.milestone_type || 'default'));
        });

    // Calculate timeline range
    let timelineRange;
    if (customDateRange?.startDate && customDateRange?.endDate) {
      // Use custom range
      const start = new Date(customDateRange.startDate);
      const end = new Date(customDateRange.endDate);
      const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      
      timelineRange = {
        startDate: customDateRange.startDate,
        endDate: customDateRange.endDate,
        totalDays
      };
    } else {
      // Auto-calculate from items (existing behavior)
      timelineRange = getTimelineRange(filteredItems);
    }
    
    // Paginate timeline for A4 printing
    const pages = paginateTimeline(filteredItems, { 
      pageFormat,
      customTimelineRange: customDateRange,
      stretchToFullWidth,
      includeCategory: showCategories,
      includeProject: isMultiProject
    });

    // Multi-project grouping
    let projectGroups: Array<{ projectId: string; projectName: string; items: TimelineItem[] }> | undefined;
    const projectMap = new Map<string, { projectName: string; count: number; totalDuration: number }>();
    
    if (isMultiProject) {
      const projectItemsMap = new Map<string, TimelineItem[]>();
      
      filteredItems.forEach(item => {
        const projectId = item.projectId || 'unknown';
        if (!projectItemsMap.has(projectId)) {
          projectItemsMap.set(projectId, []);
        }
        projectItemsMap.get(projectId)!.push(item);
      });
      
      projectGroups = Array.from(projectItemsMap.entries()).map(([projectId, items]) => ({
        projectId,
        projectName: items[0]?.projectName || projectId,
        items
      }));
    }

    // Calculate statistics
    const categoryMap = new Map<string, { count: number; totalDuration: number; color: string }>();
    
    // Initialize categories from data
    categoriesData.forEach(cat => {
      categoryMap.set(cat.name, {
        count: 0,
        totalDuration: 0,
        color: cat.color
      });
    });

    let totalDuration = 0;
    
    filteredItems.forEach(item => {
      const startDate = new Date(item.startDate);
      const endDate = new Date(item.endDate);
      const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      
      totalDuration += duration;
      
      // Category stats
      const categoryStats = categoryMap.get(item.category) || {
        count: 0,
        totalDuration: 0,
        color: '#6b7280'
      };
      categoryStats.count += 1;
      categoryStats.totalDuration += duration;
      categoryMap.set(item.category, categoryStats);
      
      // Project stats (for multi-project)
      if (isMultiProject && item.projectId) {
        const projectStats = projectMap.get(item.projectId) || {
          projectName: item.projectName || item.projectId,
          count: 0,
          totalDuration: 0
        };
        projectStats.count += 1;
        projectStats.totalDuration += duration;
        projectMap.set(item.projectId, projectStats);
      }
    });

    const categoryBreakdown = Array.from(categoryMap.entries())
      .filter(([_, stats]) => stats.count > 0)
      .map(([category, stats]) => ({
        category,
        count: stats.count,
        totalDuration: stats.totalDuration,
        color: stats.color
      }))
      .sort((a, b) => b.count - a.count);

    const projectBreakdown = isMultiProject 
      ? Array.from(projectMap.entries()).map(([projectId, stats]) => ({
          projectId,
          projectName: stats.projectName,
          count: stats.count,
          totalDuration: stats.totalDuration
        })).sort((a, b) => b.count - a.count)
      : undefined;

    const statistics = {
      totalItems: filteredItems.length,
      totalDuration,
      averageDuration: filteredItems.length > 0 ? Math.round(totalDuration / filteredItems.length) : 0,
      projectCount: isMultiProject ? projectMap.size : undefined,
      categoryBreakdown,
      projectBreakdown
    };
    
    // Group milestones by item
    const milestonesByItem = milestones.reduce((acc, m) => {
      if (!acc[m.item_id]) acc[m.item_id] = [];
      acc[m.item_id].push(m);
      return acc;
    }, {} as Record<string, typeof milestones>);

    // ✅ RETIRED PR-F3: Assessment always calculated (flag hardcoded true)
    const calculationStart = performance.now();
    
    // Build category colors map
    const categoryColors: Record<string, string> = {};
    categoriesData.forEach(cat => {
      categoryColors[cat.name] = cat.color;
    });
    
    // Calculate assessments
    const projectHealth = calculateProjectHealth(filteredItems, milestonesByItem as Record<string, ItemMilestone[]>);
    const categoryPerformance = calculateCategoryPerformance(
      filteredItems, 
      milestonesByItem as Record<string, ItemMilestone[]>,
      categoryColors
    );
    const issues = generateAssessmentIssues(projectHealth, categoryPerformance);
    
    const assessment = {
      projectHealth,
      categoryPerformance,
      issues
    };
    
    const calculationTime = performance.now() - calculationStart;
    if (import.meta.env.DEV) {
      console.log(`[Timeline Assessment] Calculation time: ${calculationTime.toFixed(2)}ms`);
    }
    
    if (calculationTime > 500 && import.meta.env.DEV) {
      console.warn('[Timeline Assessment] Calculation time exceeded 500ms:', calculationTime);
    }

    return {
      filteredItems,
      pages,
      timelineRange,
      statistics,
      projectGroups,
      milestones,
      milestonesByItem,
      milestoneTypes: milestoneTypesData,
      assessment,
      itemDeps,
      msDeps
    };
  }, [items, filters, activeFunctions, selectedProject, pageFormat, sortOrder, customDateRange, stretchToFullWidth, showCategories, categoriesData, canViewCategory, milestones, milestoneTypesData, itemDeps, msDeps]);

  return reportData;
};